/**
 * Shared page-context providers for the listing pages.
 *
 * One provider per MECHANISM, not per page: every listing page runs the same DataTables +
 * shoppingCart + search-form infrastructure, so a single implementation with a small
 * route-keyed config covers them all. Providers read component state through JS APIs
 * (DataTables row data, shoppingCart selection, the storage store) — never markup text,
 * which is localized and changes shape.
 *
 * Self-registering on import: no page code is touched (legacy_js is frozen). When a page
 * migrates to React it registers its own provider and its row is removed from the config
 * here. All values are hints — the backend re-validates every reference against the
 * user's tenant and re-derives labels server-side.
 */
import pageContext from './pageContext';

// The applications listing keeps its position filter in the search-form state (URL
// fragment / storage), never in the path — the backend cannot see it any other way.
// Sentinel values mirror the search form's non-job options ("show archived", "shared
// with me"); keep in sync with POSITION_SENTINEL_VALUES in wp_pages/searchform.jsx.
const POSITION_FILTER_SENTINELS = new Set(['-1', '-2', -1, -2]);

// hashKey names the row field carrying the hashed id — the serializers disagree:
// applications/positions/talents rows use "hash", requisitions/introductions use "id".
// The talent-community listing and its groups page render DIFFERENT rows into the SAME
// table id, so the route (not the selector) decides the kind.
const LISTING_TABLES = {
    '/applications': { selector: '#talents_datatable', kind: 'application' },
    '/positions': { selector: '#positions_datatable', kind: 'position' },
    '/talent-community': { selector: '#tc_talents_datatable', kind: 'talent' },
    '/talent-community/groups': { selector: '#tc_talents_datatable', kind: 'talent_community_group' },
    '/jobrequisitions': { selector: '#requisitions_datatable', kind: 'job_requisition', hashKey: 'id' },
    '/introductions': { selector: '#introductions_datatable', kind: 'candidate_introduction', hashKey: 'id' },
};

// Selection storage is per shoppingCart context; only these contexts map to a kind the
// backend accepts.
const SELECTION_KINDS = {
    applications: 'application',
    talents: 'talent',
    talent_community_groups: 'talent_community_group',
};

const entityUrn = (kind, hash) => `urn:talentadore:${kind}:${hash}`;

const currentDataTable = (selector) => {
    const $ = window.jQuery;
    if (!$ || !$.fn.dataTable || !$.fn.dataTable.isDataTable(selector)) {
        return null;
    }
    return $(selector).DataTable();
};

/** Rows on the current DataTable page, as URNs, plus the result totals. */
const listingProvider = () => {
    const config = LISTING_TABLES[window.location.pathname];
    if (!config) {
        return null;
    }
    const table = currentDataTable(config.selector);
    if (!table) {
        return null;
    }
    const hashKey = config.hashKey || 'hash';
    const items = table
        .rows({ page: 'current' })
        .data()
        .toArray()
        .filter((row) => row && typeof row[hashKey] === 'string')
        .map((row) => entityUrn(config.kind, row[hashKey]));
    if (!items.length) {
        return null;
    }
    const info = table.page.info();
    return {
        visible: {
            kind: config.kind,
            items,
            total: info.recordsDisplay,
            page_size: info.length,
        },
    };
};

/** The user's checked rows (shoppingCart selection), as URNs. */
const selectionsProvider = () => {
    const cart = window.shoppingCart;
    if (!cart || typeof cart.getSelection !== 'function') {
        return null;
    }
    const kind = SELECTION_KINDS[cart.getContext?.()];
    if (!kind) {
        return null;
    }
    const hashes = (cart.getSelection() || []).filter((value) => typeof value === 'string');
    if (!hashes.length) {
        return null;
    }
    return { selected: hashes.map((hash) => entityUrn(kind, hash)) };
};

/** The single position the applications listing is filtered to, when there is exactly one. */
const positionFilterProvider = () => {
    // The stored filter belongs to the applications listing; on any other page it is
    // stale state from an earlier visit, not what the user is looking at.
    if (window.location.pathname !== '/applications') {
        return null;
    }
    const store = window.storageStore;
    if (!store?.applications?.getSelectedPositions) {
        return null;
    }
    const values = store.applications.getSelectedPositions() || [];
    const positions = values.filter((value) => !POSITION_FILTER_SENTINELS.has(value));
    if (positions.length !== 1 || typeof positions[0] !== 'string') {
        return null;
    }
    return { current_job: entityUrn('position', positions[0]) };
};

/** Kanban vs list on the applications listing, as the page shows it now — published by
 * legacy_js/applications.js as `getApplicationsViewType`. Neither the stored choice (a visit can
 * show the list without changing it) nor the kanban component (mounted-but-hidden in list mode)
 * is the shown view; until the page has published it, the view is unknown. */
const viewModeProvider = () => {
    if (window.location.pathname !== '/applications' || typeof window.getApplicationsViewType !== 'function') {
        return null;
    }
    return { view_mode: window.getApplicationsViewType() === 'kanban' ? 'kanban' : 'list' };
};

// The application and talent detail pages share the same two-tab-group layout (identical
// markup classes); both records already resolve server-side from the route.
const APPLICANT_PAGE_ROUTE = /^\/(applications|talents)\/[^/]+$/;

const activeTabId = ($anchor) => {
    const href = $anchor?.attr('href');
    if (typeof href !== 'string' || !href.startsWith('#')) {
        return null;
    }
    // Notepad tabs carry per-company ids; their semantic identity is just "a notepad".
    return $anchor.hasClass('notepad-title') ? 'notepad' : href.slice(1);
};

/** Active tabs on the application/talent detail pages (server-rendered; the is-active
 * anchors ARE the legacy pages' tab state — there is no JS API for it). These pages run
 * TWO independent tab groups at once: the main content tabs and the
 * evaluation/activities/notepad side panel. The open record itself comes from the route,
 * resolved server-side. */
const applicantTabProvider = () => {
    const path = window.location.pathname;
    if (!APPLICANT_PAGE_ROUTE.test(path) || path === '/applications/add_') {
        return null;
    }
    const $ = window.jQuery;
    if (!$) {
        return null;
    }
    const mainTab = activeTabId($('.tabs.applicant-tabs a.is-active'));
    const sideTab = activeTabId($('.tabs.applicant-right-tabs a.is-active'));
    if (!mainTab && !sideTab) {
        return null;
    }
    return {
        ...(mainTab && { active_tab: mainTab }),
        ...(sideTab && { active_side_tab: sideTab }),
    };
};

pageContext.register('listing_table', listingProvider);
pageContext.register('selections', selectionsProvider);
pageContext.register('position_filter', positionFilterProvider);
pageContext.register('view_mode', viewModeProvider);
pageContext.register('applicant_tab', applicantTabProvider);

export { applicantTabProvider, listingProvider, positionFilterProvider, selectionsProvider, viewModeProvider };
